import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Observable,
  Subject,
  takeUntil,
  forkJoin,
  of,
  catchError,
  map,
  finalize,
} from 'rxjs';
import { DocumentSubType } from 'src/app/core/interfaces/invoice.interface';
import { PaymentVoucherService } from 'src/app/core/services/payment-voucher.service';
import { BranchService } from 'src/app/core/services/branch.service';
import { FiltersListService } from 'src/app/core/services/filters-list.service';
import { InvoiceService } from 'src/app/core/services/invoice.service';
import { UserMessagingService } from 'src/app/core/services/user-messaging.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ActionType } from 'src/app/core/enums/action-type.enum';
import { ReceiptVoucherService } from 'src/app/core/services/receipt-voucher.service';


@Component({
  selector: 'app-payment-voucher-modal',
  templateUrl: './payment-voucher-modal.component.html',
  styleUrl: './payment-voucher-modal.component.scss',
})
export class PaymentVoucherModalComponent implements OnInit, OnDestroy {
  @Input() invoiceId: number;
  @Input() invoiceSerialNumber: string = '';
  @Input() invoiceContactId: number | null = null;
  @Input() invoiceContactName: string = '';
  @Input() invoiceTotalAmount: number = 0;
  @Input() invoiceDate: string = '';
  @Input() invoiceNote: string = '';
  @Input() isReceiptVoucher: boolean = true;
  @Input() invoicePushedAmount?: number = 0;
   @Input() reminderAmount?: number;
  @Input() invoiceRemaining?: number = 0;
  @Input() IsListInvoice: boolean = false;
  invoiceType: string = 'Sales';

  voucherForm: FormGroup;
  isLoading = false;
  paymentMethods: any[] = [];
  paymentAccounts: any[] = [];
  refreshPaymentAccounts = 0;
  private branchId: number | null = null;
  private branchData: any = null;

  private destroy$ = new Subject<void>();

  get currentLanguage(): string {
    return this.localizationService.getCachedLanguage() || 'ar';
  }

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private paymentVoucherService: PaymentVoucherService,
    private receiptVoucherService: ReceiptVoucherService,
    private branchService: BranchService,
    private filtersListService: FiltersListService,
    private invoiceService: InvoiceService,
    private userMessagingService: UserMessagingService,
    private localizationService: LocalizationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadInitialData();
  }

  get paymentTypeControl(): FormControl {
    return this.voucherForm.get('PaymentType') as FormControl;
  }

  get cashboxControl(): FormControl {
    return this.voucherForm.get('CashboxId') as FormControl;
  }

  private initForm(): void {
    if (this.isReceiptVoucher) {
    }
    
    this.voucherForm = this.fb.group({
      journalDate: [
        this.invoiceDate || new Date().toISOString().split('T')[0],
        Validators.required,
      ],
      description: [this.invoiceNote || ''],
      refNumber: [this.invoiceSerialNumber || ''],
      PaymentType: [null, Validators.required],
      CashboxId: [null, Validators.required],
      amount: [
        this.reminderAmount ?? this.invoiceRemaining ?? this.invoiceTotalAmount,
        [Validators.required, Validators.min(0.0001)],
      ],
      accountId: [this.invoiceContactId, Validators.required],
    });
  }
  onPaymentTypeChange(paymentType: any): void {
    const methodId = Number(paymentType?.id ?? paymentType);
    if (methodId) {
      const selectedMethod = this.paymentMethods.find(
        (method) => Number(method?.id ?? method?.key) === methodId
      );
      if (selectedMethod) {
        this.onCurrentPaymentTypeSelected(selectedMethod);
        return;
      }
    }

    this.voucherForm
      .get('PaymentType')
      ?.setValue(methodId, { emitEvent: false });
    this.voucherForm.get('CashboxId')?.setValue(null);
    this.voucherForm.get('CashboxId')?.setErrors(null);
    this.refreshPaymentAccounts += 1;
    this.cdr.detectChanges();
  }

  private loadInitialData(): void {
    this.isLoading = true;

    forkJoin({
      branch: this.branchService
        .getDefaultBranch(false)
        .pipe(catchError(() => of(null))),
      paymentMethods: this.invoiceService
        .getPaymentMethodLookup(1, 100, '')
        .pipe(catchError(() => of([]))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ branch, paymentMethods }) => {
          this.branchId = branch?.data?.id ?? null;
          this.branchData = branch?.data || null;

          this.paymentMethods = Array.isArray(paymentMethods)
            ? paymentMethods
            : (paymentMethods as any)?.data ||
              (paymentMethods as any)?.items ||
              [];

          const currentPaymentType = Number(
            this.voucherForm.get('PaymentType')?.value || 0
          );
          if (!currentPaymentType && this.paymentMethods.length > 0) {
            const cashMethod =
              this.paymentMethods.find(
                (method: any) =>
                  Number(method?.id ?? method?.key) === 1 ||
                  String(
                    method?.nameEn ?? method?.labelEn ?? ''
                  ).toLowerCase() === 'cash' ||
                  String(method?.nameAr ?? method?.labelAr ?? '').trim() ===
                    'نقدي'
              ) || this.paymentMethods[0];

            if (cashMethod) {
              this.voucherForm
                .get('PaymentType')
                ?.setValue(Number(cashMethod?.id ?? cashMethod?.key), {
                  emitEvent: false,
                });
            }
          }

          if (
            this.isCashPaymentMethod(this.voucherForm.get('PaymentType')?.value)
          ) {
            this.applyDefaultPaymentAccountForCash(false);
          }

          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private isDeferredPaymentMethodItem(item: any): boolean {
    if (!item) {
      return false;
    }

    const id = Number(item?.id ?? item?.key);
    const normalizedType = String(
      item?.type ??
        item?.nameEn ??
        item?.labelEn ??
        item?.nameAr ??
        item?.labelAr ??
        ''
    )
      .trim()
      .toLowerCase();
    const normalizedTypeAr = String(
      item?.typeAr ?? item?.nameAr ?? item?.labelAr ?? ''
    ).trim();

    return (
      id === 8 ||
      normalizedType === 'debit' ||
      normalizedTypeAr === 'آجل' ||
      normalizedTypeAr === 'مدين'
    );
  }

  fetchPaymentTypes = (
    pageNumber = 1,
    pageSize = 10,
    term = ''
  ): Observable<any> => {
    return this.invoiceService
      .getPaymentMethodLookup(pageNumber, pageSize, term)
      .pipe(
        map((response: any) => {
          const mapped = Array.isArray(response)
            ? response
            : response?.data || [];
          const filtered = mapped.filter(
            (item: any) => !this.isDeferredPaymentMethodItem(item)
          );
          filtered.forEach((item: any) => {
            const methodId = Number(item?.id ?? item?.key);
            if (
              !this.paymentMethods.some(
                (el) => Number(el?.id ?? el?.key) === methodId
              )
            ) {
              this.paymentMethods.push(item);
            }
          });
          return Array.isArray(response)
            ? filtered
            : { ...response, data: filtered };
        })
      );
  };

  fetchPaymentTypeById = (id: number | string): Observable<any> => {
    const normalizedId = Number(id);
    if (!normalizedId) {
      return of(null);
    }

    const cached = this.paymentMethods.find(
      (method: any) => Number(method?.id ?? method?.key) === normalizedId
    );
    if (cached) {
      return of(cached);
    }

    return this.invoiceService
      .getPaymentMethodLookup(1, 1, '', [
        {
          logic: 'AND',
          field: 'id',
          operator: 'EQ',
          value: String(normalizedId),
        },
      ])
      .pipe(
        map((response: any) => {
          const mapped = Array.isArray(response)
            ? response
            : response?.data || [];
          const item = mapped[0] || null;
          if (item) {
            const methodId = Number(item?.id ?? item?.key);
            if (
              !this.paymentMethods.some(
                (el) => Number(el?.id ?? el?.key) === methodId
              )
            ) {
              this.paymentMethods.push(item);
            }
          }
          return item;
        })
      );
  };

  fetchPaymentAccounts = (
    pageNumber = 1,
    pageSize = 10,
    term = ''
  ): Observable<any[]> => {
    const filters: any[] = this.getPaymentAccountFilters();
    return this.filtersListService
      .getAccountsFn(pageNumber, pageSize, term, filters)
      .pipe(
        map((response: any) =>
          Array.isArray(response) ? response : response?.data || []
        )
      );
  };

  fetchPaymentAccountById = (id: number): Observable<any> => {
    const cached = this.paymentAccounts.find(
      (account: any) => Number(account?.id ?? account?.key) === Number(id)
    );
    if (cached) {
      return of(cached);
    }

    return this.filtersListService
      .getAccountsFn(1, 1, '', [
        { logic: 'AND', field: 'id', operator: 'EQ', value: String(id) },
      ])
      .pipe(
        map((accounts: any) => {
          const item = Array.isArray(accounts)
            ? accounts[0]
            : accounts?.data?.[0] || null;
          if (item) {
            this.paymentAccounts = [...this.paymentAccounts, item];
          }
          return item;
        })
      );
  };

  onCurrentPaymentTypeSelected(option: any): void {
    const id = Number(option?.id ?? option?.key);
    if (id) {
      this.voucherForm.get('PaymentType')?.setValue(id);
      if (!this.paymentMethods.some((el) => Number(el?.id ?? el?.key) === id)) {
        this.paymentMethods = [...this.paymentMethods, option];
      }
    }

    this.voucherForm.get('CashboxId')?.setValue(null);
    this.voucherForm.get('CashboxId')?.setErrors(null);
    this.refreshPaymentAccounts += 1;
    this.cdr.detectChanges();

    if (this.isCashPaymentMethod(id)) {
      setTimeout(() => this.applyDefaultPaymentAccountForCash(false), 0);
    }
  }

  onCurrentPaymentAccountSelected(option: any): void {
    const optionId = Number(option?.id ?? option?.key);
    if (
      optionId &&
      !this.paymentAccounts.some(
        (account: any) => Number(account?.id ?? account?.key) === optionId
      )
    ) {
      this.paymentAccounts = [...this.paymentAccounts, option];
    }
    this.voucherForm.get('CashboxId')?.setErrors(null);
  }

  private getPaymentAccountFilters(): any[] {
    const categoryType = this.resolveGlAccountCategoryTypeByPaymentMethod();
    if (!categoryType) {
      return [];
    }

    return [
      {
        logic: 'AND',
        field: 'GlAccountCategoryType',
        operator: 'EQ',
        value: categoryType,
      },
    ];
  }

  private resolveGlAccountCategoryTypeByPaymentMethod():
    | 'BOXES'
    | 'BANKS'
    | 'CUSTOMERS'
    | 'VENDORS'
    | null {
    const selectedPaymentType = Number(
      this.voucherForm.get('PaymentType')?.value
    );
    if (!selectedPaymentType) {
      return null;
    }

    if (this.isCashPaymentMethod(selectedPaymentType)) {
      return 'BOXES';
    }

    if (this.isDebitPaymentMethod(selectedPaymentType)) {
      return this.isReceiptVoucher ? 'CUSTOMERS' : 'VENDORS';
    }

    return 'BANKS';
  }

  private applyDefaultPaymentAccountForCash(
    replaceExistingAccount = false
  ): void {
    const paymentType = this.voucherForm.get('PaymentType')?.value;
    if (!this.isCashPaymentMethod(paymentType)) {
      return;
    }

    const currentAccountId = Number(
      this.voucherForm.get('CashboxId')?.value ?? 0
    );
    if (!replaceExistingAccount && currentAccountId > 0) {
      return;
    }

    const preferredId = this.getBranchDefaultCashAccountIdForCreate();
    this.fetchPaymentAccounts(1, 100, '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (accounts: any) => {
          const selectedAccounts = Array.isArray(accounts) ? accounts : [];
          let selectedId: number | null = null;

          if (
            preferredId &&
            selectedAccounts.some(
              (a: any) => Number(a?.id ?? a?.key) === preferredId
            )
          ) {
            selectedId = preferredId;
          } else if (selectedAccounts.length === 1) {
            selectedId =
              Number(selectedAccounts[0]?.id ?? selectedAccounts[0]?.key) ||
              null;
          }

          if (selectedId != null) {
            this.voucherForm
              .get('CashboxId')
              ?.setValue(selectedId, { emitEvent: false });
            this.voucherForm.get('CashboxId')?.setErrors(null);
            this.refreshPaymentAccounts += 1;
            this.cdr.detectChanges();
            return;
          }

          if (preferredId) {
            this.fetchPaymentAccountById(preferredId)
              .pipe(takeUntil(this.destroy$))
              .subscribe((acc: any) => {
                const accNumericId = Number(acc?.id ?? acc?.key ?? 0);
                if (acc && accNumericId) {
                  this.voucherForm
                    .get('CashboxId')
                    ?.setValue(accNumericId, { emitEvent: false });
                  this.voucherForm.get('CashboxId')?.setErrors(null);
                  this.refreshPaymentAccounts += 1;
                  this.cdr.detectChanges();
                }
              });
          }
        },
      });
  }

  private getBranchDefaultCashAccountIdForCreate(): number | null {
    const raw = this.branchData?.cashAccountId;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  isCashPaymentMethod(paymentType: number | string): boolean {
    const id = Number(paymentType);
    if (!id) {
      return false;
    }
    const method = this.paymentMethods.find(
      (m) => Number(m?.id ?? m?.key) === id
    );
    if (method) {
      const nameEn = String(method.nameEn ?? method.labelEn ?? '')
        .trim()
        .toLowerCase();
      const nameAr = String(method.nameAr ?? method.labelAr ?? '').trim();
      return nameEn === 'cash' || nameAr === 'نقدي';
    }
    return id === 1;
  }

  isDebitPaymentMethod(paymentType: number | string): boolean {
    const id = Number(paymentType);
    if (!id) {
      return false;
    }
    const method = this.paymentMethods.find(
      (m) => Number(m?.id ?? m?.key) === id
    );
    if (method) {
      const nameEn = String(method.nameEn ?? method.labelEn ?? '')
        .trim()
        .toLowerCase();
      const nameAr = String(method.nameAr ?? method.labelAr ?? '').trim();
      return (
        nameEn === 'debit' ||
        nameAr === 'مدين' ||
        nameEn === 'credit' ||
        nameAr === 'آجل'
      );
    }
    return id === 8;
  }

  getAccountLabel(account: any): string {
    return this.currentLanguage === 'ar'
      ? account?.nameAr || account?.labelAr || ''
      : account?.nameEn || account?.label || '';
  }

  getPaymentMethodLabel(method: any): string {
    return this.currentLanguage === 'ar'
      ? method?.typeAr || method?.nameAr || method?.type || ''
      : method?.typeEn || method?.nameEn || method?.type || '';
  }

  submit(): void {
    this.voucherForm.markAllAsTouched();
    if (this.voucherForm.invalid) {
      return;
    }

    const fv = this.voucherForm.getRawValue();

    const payload: any = {
      journalDate: this.formatDate(fv.journalDate),
      description: fv.description ?? '',
      refNumber: fv.refNumber ?? '',
      branchId: this.branchId,
      accountId: fv.CashboxId,
      cashBoxId: fv.CashboxId,
      paymentMethod: Number(fv.PaymentType ?? 1),
      isHold: false,
      isPosted: true,
      linkedDocumentId: this.invoiceId,
      lines: [
        {
          accountId: fv.accountId,
          amount: fv.amount,
          comment: fv.description ?? '',
          refNumber: fv.refNumber ?? '',
        },
      ],
    };

    this.isLoading = true;
    this.cdr.detectChanges();

    const request$ = this.isReceiptVoucher
      ? this.receiptVoucherService.createVoucher(payload)
      : this.paymentVoucherService.createVoucher(payload);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.userMessagingService.createUpdateSuccessMessage(ActionType.CREATE);
        this.activeModal.close({ created: true, voucherId: res?.data?.id });
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  close(): void {
    this.activeModal.dismiss('closed');
  }

  private formatDate(date: any): string {
    if (!date) return new Date().toISOString().split('T')[0];
    if (typeof date === 'string') return date.split('T')[0];
    if (date instanceof Date) return date.toISOString().split('T')[0];
    return new Date().toISOString().split('T')[0];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
